import React, { useState, useEffect } from "react";
import { CloseOutlined, SwapOutlined, LinkOutlined } from "@ant-design/icons";
import { declineOutgoingCall, getPiopiy } from "../../utils/piopiy";
import RedirectCallModal from "./RedirectCallModal";

function OutgoingCallPopup({
  flag,
  setflag,
  attended,
  mobileNumber = "9876543210",
  preferredExecutiveId = null,
  onTransferStarted,
  transferStatusText = "",
  transferPending = false,
  onMergeTransfer,
  onReject,
}) {
  const [callDuration, setCallDuration] = useState(0);
  const [animationState, setAnimationState] = useState("entering");
  const [redirectOpen, setRedirectOpen] = useState(false);

  useEffect(() => {
    let intervalId;
    if (attended) {
      intervalId = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(intervalId);
  }, [attended]);

  useEffect(() => {
    if (flag) {
      setCallDuration(0);
      setAnimationState("entering");
      setRedirectOpen(false);
    }
  }, [flag]);

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleReject = () => {
    setAnimationState("exiting");
    setTimeout(() => {
      setCallDuration(0);
      declineOutgoingCall(getPiopiy());
      setflag(false);
      if (onReject) onReject();
    }, 300);
  };

  if (!flag) return null;

  // Restore previous transparent call-bar look (navbar overlay)
  const containerStyle = {
    zIndex: 100000,
    backgroundColor: "transparent",
    padding: "0.5rem",
    position: "fixed",
    border: "none",
    borderRadius: "0",
    left: "45%",
    top: "0px",
    transform: "translateX(-50%)",
    boxShadow: "none",
    width: transferPending ? "340px" : "250px",
    maxWidth: "90vw",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    transition: "all 0.3s ease",
    height: "auto",
    minHeight: "50px",
    animation: `${
      animationState === "entering"
        ? "fadeInDown 0.3s forwards"
        : "fadeOutUp 0.3s forwards"
    }`,
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: "0",
  };

  const avatarStyle = {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    objectFit: "cover",
    border: "1px solid #f0f0f0",
  };

  const callerInfoStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: 1,
    marginLeft: "0.5rem",
    marginRight: "0.5rem",
  };

  const callerNameStyle = {
    fontWeight: "600",
    fontSize: "13px",
    color: "#333",
    marginBottom: "1px",
    textAlign: "center",
  };

  const callerStatusStyle = {
    color: "#888",
    fontSize: "11px",
    animation: "pulse 1.5s infinite",
    textAlign: "center",
  };

  const buttonContainerStyle = {
    display: "flex",
    justifyContent: "center",
    gap: "0.5rem",
    alignItems: "center",
  };

  const getButtonStyle = (backgroundColor) => ({
    backgroundColor,
    border: "none",
    borderRadius: "50%",
    width: "28px",
    height: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    color: "white",
    fontSize: "14px",
    boxShadow: "none",
  });

  const handleButtonHover = (e, scale) => {
    e.currentTarget.style.transform = `scale(${scale})`;
  };

  return (
    <>
      <div style={containerStyle}>
        <div style={headerStyle}>
          <img
            src={
              "https://img.freepik.com/premium-vector/social-media-logo_1305298-29989.jpg?ga=GA1.1.1780620384.1735468347&semt=ais_hybrid"
            }
            alt={"userImage"}
            style={avatarStyle}
          />
          <div style={callerInfoStyle}>
            <div style={callerNameStyle}>{mobileNumber}</div>
            <div style={callerStatusStyle}>
              {attended
                ? `Call Duration: ${formatTime(callDuration)}`
                : "Outgoing Call"}
            </div>
          </div>
          <div style={buttonContainerStyle}>
            {attended && !transferPending && (
              <button
                onClick={() => setRedirectOpen(true)}
                style={getButtonStyle("#1890ff")}
                onMouseOver={(e) => handleButtonHover(e, 1.1)}
                onMouseOut={(e) => handleButtonHover(e, 1)}
                title="Redirect to employee mobile"
              >
                <SwapOutlined />
              </button>
            )}
            {transferPending && (
              <button
                onClick={() => onMergeTransfer && onMergeTransfer()}
                style={getButtonStyle("#52c41a")}
                onMouseOver={(e) => handleButtonHover(e, 1.1)}
                onMouseOut={(e) => handleButtonHover(e, 1)}
                title="Connect customer to employee"
              >
                <LinkOutlined />
              </button>
            )}
            <button
              onClick={handleReject}
              style={getButtonStyle("#E25D53")}
              onMouseOver={(e) => handleButtonHover(e, 1.1)}
              onMouseOut={(e) => handleButtonHover(e, 1)}
              title="Hang up"
            >
              <CloseOutlined />
            </button>
          </div>
        </div>
        {transferStatusText ? (
          <div
            style={{
              marginTop: 4,
              width: "100%",
              fontSize: 11,
              color: "#096dd9",
              fontWeight: 600,
              textAlign: "center",
              lineHeight: 1.3,
            }}
          >
            {transferStatusText}
          </div>
        ) : null}

        <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeOutUp {
          from {
            opacity: 1;
            transform: translateY(0);
          }
          to {
            opacity: 0;
            transform: translateY(-20px);
          }
        }
      `}</style>
      </div>

      <RedirectCallModal
        visible={redirectOpen}
        onCancel={() => setRedirectOpen(false)}
        preferredExecutiveId={preferredExecutiveId}
        onTransferStarted={(emp) => {
          setRedirectOpen(false);
          if (onTransferStarted) onTransferStarted(emp);
        }}
      />
    </>
  );
}

export default OutgoingCallPopup;
